'use client';

import { IconButton, Spinner, Text, Tooltip } from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { differenceInSeconds, parseISO } from 'date-fns';
import { SquareIcon } from '@terra/ui-icons';
import { useEffect, useState } from 'react';
import {
  ACTIVE_STATUSES,
  type CrawlerSession,
  type CrawlerSessionStats,
} from '../../hooks/useCrawlerSessions';
import { CrawlerInlineStats } from './CrawlerInlineStats';
import { CrawlerTypeIndicator } from './CrawlerTypeIndicator';

interface CrawlerSessionCardProps {
  session: CrawlerSession;
  stats?: CrawlerSessionStats;
  onAbort?: () => void;
  isAborting?: boolean;
}

function formatElapsedTime(createdAt: string): string {
  try {
    const elapsed = differenceInSeconds(new Date(), parseISO(createdAt));
    const hours = Math.floor(elapsed / 3600);
    const minutes = Math.floor((elapsed % 3600) / 60);
    const seconds = elapsed % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  } catch {
    return '0:00';
  }
}

export function CrawlerSessionCard({
  session,
  stats,
  onAbort,
  isAborting = false,
}: CrawlerSessionCardProps) {
  const [elapsedTime, setElapsedTime] = useState(() =>
    formatElapsedTime(session.createdAt),
  );

  useEffect(() => {
    if (!ACTIVE_STATUSES.includes(session.status)) {
      return;
    }

    const interval = setInterval(() => {
      setElapsedTime(formatElapsedTime(session.createdAt));
    }, 1000);

    return () => clearInterval(interval);
  }, [session.createdAt, session.status]);
  const isActive = ACTIVE_STATUSES.includes(session.status);
  const isAbortingState = isAborting || session.status === 'ABORTING';
  const canAbort = session.crawlerType !== 'MANUAL' && isActive;
  // Calculate totals for display
  const harTotal = stats ? stats.harEntriesNew + stats.harEntriesExisting : 0;
  const pagesTotal = stats ? stats.pagesNew + stats.pagesExisting : 0;

  return (
    <VStack w="full" gap="0" py="1" px="2" minH="32px">
      <HStack w="full" justifyContent="space-between" alignItems="center">
        <HStack gap="3" alignItems="center" flex="1">
          <CrawlerTypeIndicator type={session.crawlerType} size="sm" />

          <CrawlerInlineStats harTotal={harTotal} pagesTotal={pagesTotal} />
        </HStack>

        <HStack gap="2" alignItems="center">
          {isActive && (
            <Text
              fontSize="xs"
              fontFamily="mono"
              fontWeight="medium"
              color="fg.muted"
              minW="40px"
              textAlign="right"
            >
              {elapsedTime}
            </Text>
          )}

          {canAbort && onAbort && (
            <Tooltip
              positioning={{ placement: 'top' }}
              title={isAbortingState ? 'Aborting...' : 'Abort crawler'}
            >
              <IconButton
                variant="ghost"
                size="xs"
                disabled={isAbortingState}
                onClick={() => {
                  if (!isAbortingState) {
                    onAbort();
                  }
                }}
                aria-label="Abort crawler"
              >
                {isAbortingState ? (
                  <Spinner size="xs" color="blue.400" />
                ) : (
                  <SquareIcon size={14} />
                )}
              </IconButton>
            </Tooltip>
          )}
        </HStack>
      </HStack>

      {session.error && (
        <Box w="full" p="1" bg="bg.error" borderRadius="sm">
          <Text fontSize="2xs" color="fg.error">
            {session.error}
          </Text>
        </Box>
      )}
    </VStack>
  );
}
